import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import { Card, Button, Flex, Typography, Empty, Spin, theme } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { clientByIdQueryOptions, useClientByIdQuery } from '@/api/auth/queries';
import { ModuleFilters, ModuleTable, ModuleEnableModal, ModuleConfigModal } from '@/components/modules';
import { DEFAULT_CLIENTS_LIST_ROUTE_SEARCH } from '@/api/auth/defaults';
import { useTenantModules } from '@/hooks/useTenantModules';

const { Text } = Typography;

export const Route = createFileRoute('/admin/tenants/$tenantId/modules')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    // Prefetch client data
    await context.queryClient.prefetchQuery(clientByIdQueryOptions(params.tenantId));
  }
});

function RouteComponent() {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { tenantId } = Route.useParams();
  const { data: clientData } = useClientByIdQuery(tenantId);
  const modules = useTenantModules(tenantId);

  return (
    <PageTransition animation="fadeInUp">
      <DetailPageHeader
        icon={<AppstoreOutlined />}
        title={t('tenants.modules.title')}
        meta={
          clientData?.client ? (
            <Text type="secondary">{t('tenants.modules.managingFor', { name: clientData.client.name })}</Text>
          ) : undefined
        }
        backTo="/admin/tenants"
        backSearch={DEFAULT_CLIENTS_LIST_ROUTE_SEARCH}
        backLabel={t('tenants.detail.backToClients')}
      />

      {/* Filters and Search */}
      <ModuleFilters
        searchQuery={modules.searchQuery}
        onSearchChange={modules.setSearchQuery}
        filterStatus={modules.filterStatus}
        onFilterStatusChange={modules.setFilterStatus}
        selectedCategory={modules.selectedCategory}
        onCategoryChange={modules.setSelectedCategory}
        availableCategories={modules.availableCategories}
        totalModules={modules.availableModules.length}
        enabledCount={modules.enabledCount}
        disabledCount={modules.availableModules.length - modules.enabledCount}
      />

      {/* Modules Table */}
      <Card
        styles={{ body: { padding: 0 } }}
      >
        {modules.isLoading ? (
          <Flex vertical align="center" gap={token.marginSM} style={{ paddingBlock: token.paddingXL }}>
            <Spin />
            <Text type="secondary">{t('tenants.modules.loadingModules')}</Text>
          </Flex>
        ) : modules.filteredModules.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ paddingBlock: token.paddingXL }}
            description={
              <Flex vertical align="center" gap={token.marginXXS}>
                <Text type="secondary">
                  {modules.hasActiveFilters
                    ? t('tenants.modules.noModulesMatch')
                    : t('tenants.modules.noModulesAvailable')}
                </Text>
                {modules.hasActiveFilters && (
                  <Button type="link" onClick={modules.clearFilters}>
                    {t('tenants.modules.clearAllFilters')}
                  </Button>
                )}
              </Flex>
            }
          />
        ) : (
          <ModuleTable
            modules={modules.filteredModules}
            enabledModuleIds={modules.enabledModuleIds}
            enabledModuleConfigs={modules.enabledModuleConfigs}
            selectedCategory={modules.selectedCategory}
            onCategoryChange={modules.setSelectedCategory}
            onToggleModule={modules.handleToggleModule}
            onOpenConfig={modules.handleOpenConfig}
            onOpenEnableConfig={modules.openEnableConfig}
            isToggling={modules.isToggling}
          />
        )}
      </Card>

      {/* Enable Module Modal */}
      <ModuleEnableModal
        open={modules.enableModalOpen}
        module={modules.selectedModule}
        onCancel={modules.closeEnableModal}
        onOk={modules.handleEnableWithConfig}
        form={modules.enableForm}
        loading={modules.enabling}
      />

      {/* Configuration Modal */}
      <ModuleConfigModal
        open={modules.configModalOpen}
        module={modules.selectedModule}
        onCancel={modules.closeConfigModal}
        onOk={modules.handleSaveConfig}
        form={modules.configForm}
        loading={modules.savingConfig}
      />
    </PageTransition>
  );
}
